/**
 * Central route classification used by the app's auth/layout guards.
 *
 * Two concepts, intentionally distinct:
 *
 * 1. PUBLIC_ROUTES
 *    Routes that do NOT require authentication. The app shell
 *    (sidebar, authed header, etc.) is hidden on these paths.
 *
 * 2. UNAUTHENTICATED_ONLY_ROUTES
 *    A subset of public routes that authenticated users should be
 *    redirected AWAY from (e.g. /login, /register, /forgot-password).
 *    Used by `useAuthRedirect`.
 *
 * Permission-gated redirects for authed users live in
 * `ProtectedRoute` + `PermissionsConstants` and are unrelated to this file.
 */

export const PUBLIC_ROUTES = [
  "/login",
  "/register",
  "/forgot-password",
  // Reached from a link in an email, which may well be opened in a browser already
  // signed in as somebody else. Public so the app shell is hidden; deliberately NOT in
  // UNAUTHENTICATED_ONLY_ROUTES, because that list REDIRECTS authenticated users away —
  // which would bounce them to /dashboard and make the reset impossible. The page ends
  // the existing session itself instead.
  "/reset-password",
  "/verify-email",
  "/lti-client-token",
  "/embed",
  // Landing page of the connection check's "separate window": not signed in yet, it receives the
  // session from the embedded page that opened it (see readiness-check/_lib/windowHandoff.ts).
  "/readiness-window",
] as const;

export const UNAUTHENTICATED_ONLY_ROUTES = [
  "/login",
  "/register",
  "/forgot-password",
] as const;

export type PublicRoute = (typeof PUBLIC_ROUTES)[number];
export type UnauthenticatedOnlyRoute =
  (typeof UNAUTHENTICATED_ONLY_ROUTES)[number];

export const isPublicRoute = (pathname: string | null): boolean => {
  if (!pathname) return false;
  return (PUBLIC_ROUTES as readonly string[]).includes(pathname);
};

export const isUnauthenticatedOnlyRoute = (
  pathname: string | null,
): boolean => {
  if (!pathname) return false;
  return (UNAUTHENTICATED_ONLY_ROUTES as readonly string[]).includes(pathname);
};
